import React, { type ReactNode, type ComponentProps, useState } from 'react'

import { Button } from '../common/Button'
import { ModalBox } from '../partials/ModalBox'

export type ConfirmModalProps = {
  title: ReactNode
  text?: ReactNode
  additionalContent?: ReactNode
  disabled?: boolean
  confirmLabel?: string
  cancelLabel?: string
  confirmAppearance?: 'primary' | 'secondary' | 'destructive'
  size?: ComponentProps<typeof ModalBox>['size']
  maxWidth?: ComponentProps<typeof ModalBox>['maxWidth']
  confirmButtonAnalytics?: string
  close: () => void
  onClose?: () => void
  onCancel?: () => void
  onConfirm?: () => void | Promise<void>
}

export const ConfirmModal = ({
  title,
  text,
  disabled,
  additionalContent,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmAppearance = 'primary',
  size = 'small',
  maxWidth,
  confirmButtonAnalytics,
  close,
  onClose,
  onCancel,
  onConfirm,
}: ConfirmModalProps) => {
  const [isConfirming, setIsConfirming] = useState(false)

  const handleCancelClick = () => {
    // NOTE: in order to allow opening different modals in onCancel we have to call close before it
    close()

    if (onCancel) {
      onCancel()
    }
  }

  const handleConfirmClick = async () => {
    setIsConfirming(true)

    // NOTE: in order to allow opening different modals in onConfirm we have to call close before it
    close()

    if (onConfirm) {
      await onConfirm()
    }

    setIsConfirming(false)
  }

  return (
    <ModalBox
      title={title}
      size={size}
      maxWidth={maxWidth}
      buttons={[
        <Button label={cancelLabel} appearance="secondary" onClick={handleCancelClick} />,
        <Button
          label={confirmLabel}
          appearance={confirmAppearance}
          analytics={confirmButtonAnalytics}
          disabled={disabled}
          loading={isConfirming}
          onClick={handleConfirmClick}
        />,
      ]}
      onClose={onClose || handleCancelClick}
    >
      {text}
      {additionalContent}
    </ModalBox>
  )
}
